import { nextTick } from 'vue'
import {
  createRouter,
  createWebHistory,
  type RouterScrollBehavior
} from 'vue-router'
import store from '@/store'
import { getAuthClient } from '@/plugins/auth-client'

let cancelPendingHashScroll: (() => void) | null = null

const getHashElement = (hash: string): HTMLElement | null => {
  try {
    return document.querySelector(hash)
  } catch {
    return null
  }
}

const scrollToHashWhenReady = (hash: string) => {
  cancelPendingHashScroll?.()
  cancelPendingHashScroll = null

  const scrollToElement = (element: HTMLElement) => {
    const scrollElement = document.querySelector<HTMLElement>(
      '.height-scroll-fixed'
    )
    if (scrollElement) {
      scrollElement.scrollTop = element.offsetTop
    } else {
      element.scrollIntoView()
    }
  }

  const currentElement = getHashElement(hash)
  if (currentElement) {
    scrollToElement(currentElement)
    return
  }

  const observer = new MutationObserver(() => {
    const element = getHashElement(hash)
    if (!element) return
    scrollToElement(element)
    cleanup()
  })
  const timeoutId = window.setTimeout(cleanup, 5000)

  function cleanup() {
    observer.disconnect()
    window.clearTimeout(timeoutId)
    if (cancelPendingHashScroll === cleanup) {
      cancelPendingHashScroll = null
    }
  }

  observer.observe(document.body, { childList: true, subtree: true })
  cancelPendingHashScroll = cleanup
}

const scrollBehavior: RouterScrollBehavior = (to, from, savedPosition) => {
  cancelPendingHashScroll?.()
  cancelPendingHashScroll = null

  if (savedPosition) {
    return savedPosition
  }

  if (to.hash) {
    scrollToHashWhenReady(to.hash)
    return false
  }

  // ダッシュボード内のフィルタ・タブ変更では、表示位置を維持する。
  // コメントへの移動はデータの描画後にページ側が行う。
  if (to.name === 'Dashboard' && from.name === 'Dashboard') return false
  if (to.name === 'AppDashboard' && from.name === 'AppDashboard') return false

  return { left: 0, top: 0 }
}

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/learn',
      alias: '/learn/preview',
      name: 'LearningPreview',
      component: () => import('@/pages/learning-preview/index.vue'),
      meta: {
        auth: true,
        keepAlive: false,
        fullscreen: true,
        dashboardAccess: 'admin'
      }
    },
    {
      path: '/auth/verify-email',
      name: 'VerifyEmailAddress',
      component: () =>
        import(
          /* webpackChunkName: "auth-verify-email-address" */ '@/pages/auth/verify-email/index.vue'
        ),
      meta: {
        auth: false
      }
    },
    {
      path: '/auth/send-signin-email/successful',
      name: 'SendSignInEmailSuccessful',
      component: () =>
        import(
          /* webpackChunkName: "auth-confirm-send-mail-address" */ '@/pages/auth/send-signin-email/successful/index.vue'
        ),
      meta: {
        auth: false
      }
    },
    {
      path: '/auth/signin',
      name: 'AuthSignIn',
      component: () =>
        import(
          /* webpackChunkName: "auth-signin" */ '@/pages/auth/signin/index.vue'
        ),
      meta: {
        auth: false
      }
    },
    {
      path: '/auth/reset-password',
      name: 'ResetPassword',
      component: () =>
        import(
          /* webpackChunkName: "auth-renew-password" */ '@/pages/auth/reset-password/index.vue'
        ),
      meta: {
        auth: false
      }
    },
    {
      path: '/auth/reset-password-success',
      name: 'ResetPasswordSuccess',
      component: () =>
        import(
          /* webpackChunkName: "auth-renew-password-success" */ '@/pages/auth/reset-password-success/index.vue'
        ),
      meta: {
        auth: false
      }
    },
    {
      path: '/auth/firebase/callback',
      name: 'FirebaseAuthCallback',
      component: () =>
        import(
          /* webpackChunkName: "auth-firebase-auth-callback" */ '@/pages/auth/firebase/callback/index.vue'
        ),
      meta: {
        auth: false
      }
    },
    {
      path: '/skills/:skillId/questions',
      name: 'SkillQuestion',
      component: () =>
        import(
          /* webpackChunkName: "skills-skill-id-questions" */ '@/pages/skills/_skillId/questions/index.vue'
        ),
      meta: {
        auth: true
      }
    },
    {
      path: '/sp-page/:spPageId',
      name: 'SpPage',
      component: () =>
        import(
          /* webpackChunkName: "sp-page" */ '@/pages/sp/_spPageId/index.vue'
        ),
      meta: {
        auth: true
      }
    },
    {
      path: '/',
      name: 'Home',
      component: () =>
        import(/* webpackChunkName: "home" */ '@/pages/index.vue'),
      meta: {
        auth: true
      }
    },
    {
      path: '/dashboard',
      name: 'Dashboard',
      component: () =>
        import(
          /* webpackChunkName: "dashboard" */ '@/pages/dashboard/index.vue'
        ),
      meta: {
        auth: true,
        dashboardAccess: 'viewer',
        // レポート専用の全画面レイアウト（ヘッダー・サイドバーを出さない）
        fullscreen: true
      }
    },
    {
      // アプリ学習ダッシュボード（グロースＸ社内向け。閲覧可否は API 側で判定し、導線は置かない）
      path: '/app-dashboard',
      name: 'AppDashboard',
      component: () =>
        import(
          /* webpackChunkName: "app-dashboard" */ '@/pages/app-dashboard/index.vue'
        ),
      meta: {
        auth: true,
        dashboardAccess: 'admin',
        fullscreen: true
      }
    },
    {
      path: '/watch-history',
      name: 'WatchHistory',
      component: () =>
        import(
          /* webpackChunkName: "watch-history" */ '@/pages/watch-history/index.vue'
        ),
      meta: {
        auth: true
      }
    },
    {
      path: '/ai-chat',
      name: 'AiChatList',
      component: () =>
        import(
          /* webpackChunkName: "ai-chat-list" */ '@/pages/ai-chat/index.vue'
        ),
      meta: {
        auth: true,
        requireAiChat: true
      }
    },
    {
      path: '/ai-chat/:slug/:shareToken',
      name: 'AiChatShared',
      component: () =>
        import(
          /* webpackChunkName: "ai-chat" */ '@/pages/ai-chat/_slug/index.vue'
        ),
      meta: {
        auth: true,
        requireAiChat: true,
        // AiChat と同一インスタンスを使い回す。チャット中のURL正規化
        // （/{slug} ↔ /{slug}/{shareToken}）で router-view の key が変わって
        // コンポーネントが作り直され、ストリーミング中のチャットが破棄されるのを防ぐ。
        pageGroup: 'ai-chat-session'
      }
    },
    {
      path: '/ai-chat/:slug',
      name: 'AiChat',
      component: () =>
        import(
          /* webpackChunkName: "ai-chat" */ '@/pages/ai-chat/_slug/index.vue'
        ),
      meta: {
        auth: true,
        requireAiChat: true,
        pageGroup: 'ai-chat-session'
      }
    },
    {
      path: '/search',
      name: 'Search',
      component: () =>
        import(/* webpackChunkName: "search" */ '@/pages/search/index.vue'),
      meta: {
        auth: true
      }
    },
    {
      path: '/questions/:id/:idAnswer?',
      name: 'QuestionDetail',
      component: () =>
        import(
          /* webpackChunkName: "question" */ '@/pages/questions/_id/index.vue'
        ),
      meta: {
        auth: true,
        keepAlive: false
      }
    },
    {
      path: '/in-company',
      name: 'InCompany',
      component: () =>
        import(/* webpackChunkName: "in-company" */ '@/pages/index.vue'),
      meta: {
        auth: true
      }
    },
    {
      path: '/clips',
      name: 'Clips',
      component: () =>
        import(/* webpackChunkName: "clips" */ '@/pages/clips/index.vue'),
      meta: {
        auth: true
      }
    },
    {
      path: '/profile/:userHash',
      name: 'UserActivity',
      component: () =>
        import(/* webpackChunkName: "user-activity" */ '@/pages/index.vue'),
      meta: {
        auth: true,
        inCompany: true,
        userActivity: true
      }
    },
    {
      path: '/survey/:id',
      name: 'Survey',
      component: () =>
        import(/* webpackChunkName: "survey" */ '@/pages/survey/_id/index.vue')
    },
    // Not found route -> redirect to home page
    {
      path: '/:pathMatch(.*)*',
      redirect: (to) => {
        return { name: 'Home', query: { ...to.query } }
      }
    }
  ],
  scrollBehavior
})

// callbacks
router.afterEach((to, from) => {
  if (from.name) {
    store.dispatch('setState', {
      key: 'previousRoute',
      value: from.path
    })
  }

  // send user transition event log
  if (store.getters['auth/isLoggedIn']) {
    store.dispatch('sendEventLog', to.fullPath)
  }

  // scroll back after redirect from detail back to question lists page
  if (
    from.name === 'QuestionDetail' &&
    to.name &&
    ['Home', 'Search', 'SkillQuestion', 'WatchHistory'].includes(
      String(to.name)
    )
  ) {
    nextTick(() => {
      const layoutMain =
        to.name === 'WatchHistory'
          ? document.querySelector('.height-scroll-fixed')
          : document.documentElement.clientWidth >= 1200
            ? document.querySelector('.section-layout-main')
            : document.querySelector('.spContent-scroll')
      if (!layoutMain) return
      setTimeout(() => {
        layoutMain.scrollTop = store.state.scrollHistory
      }, 0)
    })
  }
})

router.beforeEach((to, _from, next) => {
  // is_allow_ai_chat=false のユーザーは AIチャット系ルートに入れない（Home へ退避）。
  // 明示的に false のときだけ弾く（未ロード/許可ユーザーは通し、最終防衛はバックエンドの ai.chat.allowed）。
  const requiresAiChat = to.matched.some((record) => record.meta.requireAiChat)
  if (requiresAiChat) {
    const auth = getAuthClient()
    const user = auth?.check() ? auth.user() : null
    if (user && user.is_allow_ai_chat === false) {
      return next({ name: 'Home' })
    }
  }
  next()
})

export default router
